/**
 * <FieldReportListRow>
 *
 * Compact table-style row shown in the List view of the Field Reports
 * page. Extracted from FieldReports.js at the same time as
 * <FieldReportCard> — both were previously inline nested components
 * inside the FieldReports function (violating react/no-unstable-
 * nested-components).
 *
 * Same button set + testids as FieldReportCard so behaviour is
 * uniform between Cards and List views.
 */
import React from 'react';
import { format } from 'date-fns';
import { Edit, Download, Mail, RotateCcw, Trash2 } from 'lucide-react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { parseLocalDate } from '../../lib/utils';
import { resolveStatus } from './status';

export default function FieldReportListRow({
  kind,
  kindLabel,
  kindColor,
  item,
  title,
  getReportDate,
  onView,
  onEdit,
  onPdf,
  onEmail,
  onReset,
  onDelete,
  isAdmin,
  PhotoReadinessDot,
}) {
  const status = resolveStatus(item.status);
  const stop = (fn) => (e) => { e.stopPropagation(); fn?.(); };
  const rawDate = getReportDate?.(item);

  return (
    <div
      className="flex items-center gap-3 px-3 py-2 border-b border-slate-100 hover:bg-slate-50 cursor-pointer"
      onClick={onView}
      data-testid={`field-report-row-${kind}-${item.id}`}
    >
      <Badge className={`${kindColor} flex-shrink-0 w-24 justify-center whitespace-nowrap`}>{kindLabel}</Badge>
      <Badge className={`flex-shrink-0 w-20 justify-center ${status.color}`}>{status.label}</Badge>
      {PhotoReadinessDot && <PhotoReadinessDot record={item} />}
      <div className="flex-1 min-w-0">
        <p className="font-medium text-slate-900 truncate">{title}</p>
        <p className="text-xs text-slate-500 truncate">
          {[
            item.assigned_team,
            rawDate ? format(parseLocalDate(rawDate) || new Date(), 'MMM d, yyyy') : '',
            item.address || item.site_location || item.location,
          ].filter(Boolean).join(' • ')}
        </p>
      </div>
      <div className="flex gap-1 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
        {onEdit && (
          <Button
            variant="ghost"
            size="sm"
            onClick={stop(onEdit)}
            title="Edit"
            data-testid={`${kind}-edit-list-${item.id}`}
          >
            <Edit className="w-4 h-4" />
          </Button>
        )}
        <Button
          variant="ghost"
          size="sm"
          onClick={stop(onPdf)}
          title="PDF"
          data-testid={`${kind}-pdf-list-${item.id}`}
        >
          <Download className="w-4 h-4 text-amber-600" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={stop(onEmail)}
          title="Email me the PDF"
          data-testid={`email-pdf-list-${kind}-${item.id}`}
        >
          <Mail className="w-4 h-4 text-blue-600" />
        </Button>
        {isAdmin && item.status === 'completed' && onReset && (
          <Button
            variant="ghost"
            size="sm"
            onClick={stop(onReset)}
            title="Reset Approval (send back to Pending Review)"
            data-testid={`reset-approval-${kind}-${item.id}`}
          >
            <RotateCcw className="w-4 h-4 text-orange-600" />
          </Button>
        )}
        {isAdmin && onDelete && (
          <Button
            variant="ghost"
            size="sm"
            onClick={stop(onDelete)}
            title="Delete"
            data-testid={`${kind}-delete-list-${item.id}`}
          >
            <Trash2 className="w-4 h-4 text-red-500" />
          </Button>
        )}
      </div>
    </div>
  );
}
